import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import { UserInfo, LoginResponse } from '../models/auth.model';

// Auth API Interfaces
export interface LoginRequest {
  email: string;
  password: string;
}


export interface SignupRequest {
  companyName: string;
  address?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  email?: string;
  adminEmail: string;
  adminPassword: string;
  adminFirstName?: string;
  adminLastName?: string;
}

export interface SignupResponse {
  tenantId: number;
  companyName: string;
  adminEmail: string;
  accessToken: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ForgotPasswordResponse {
  message: string;
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
}

export interface ResetPasswordResponse {
  message: string;
}

export interface SetupPasswordRequest {
  token: string;
  newPassword: string;
}

export interface SetupPasswordResponse {
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly authEndpoint = '/api/v1/auth';
  private readonly TOKEN_KEY = 'accessToken';

  constructor(private apiService: ApiService) {}

  /**
   * Login user
   */
  login(credentials: LoginRequest): Observable<LoginResponse> {
    return this.apiService.post<LoginResponse>(`${this.authEndpoint}/login`, credentials);
  }

  /**
   * Handle successful login - store auth data
   */
  handleLoginSuccess(response: LoginResponse): void {
    this.setAuthData(response.accessToken);
  }

  /**
   * Sign up new company
   */
  signup(signupData: SignupRequest): Observable<SignupResponse> {
    return this.apiService.post<SignupResponse>(`${this.authEndpoint}/signup`, signupData);
  }

  /**
   * Request password reset
   */
  forgotPassword(request: ForgotPasswordRequest): Observable<ForgotPasswordResponse> {
    return this.apiService.post<ForgotPasswordResponse>(`${this.authEndpoint}/forgot-password`, request);
  }

  /**
   * Reset password with token
   */
  resetPassword(request: ResetPasswordRequest): Observable<ResetPasswordResponse> {
    return this.apiService.post<ResetPasswordResponse>(`${this.authEndpoint}/reset-password`, request);
  }

  /**
   * Setup password with token
   */
  setupPassword(request: SetupPasswordRequest): Observable<SetupPasswordResponse> {
    return this.apiService.post<SetupPasswordResponse>(`${this.authEndpoint}/setup-password`, request);
  }

  /**
   * Get current user from JWT token
   * Validates JWT without making server request
   */
  getCurrentUser(): UserInfo | null {
    const token = this.getStoredToken();
    if (!token) {
      return null;
    }

    // Check if token is expired
    if (this.isTokenExpired(token)) {
      this.clearStoredData();
      return null;
    }

    // Decode user info from JWT token
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));

      // Parse roles - handle both string (JSON serialized) and array formats
      let roles: string[] = [];
      if (payload.roles) {
        if (typeof payload.roles === 'string') {
          try {
            roles = JSON.parse(payload.roles);
          } catch {
            roles = [];
          }
        } else if (Array.isArray(payload.roles)) {
          roles = payload.roles;
        }
      }

      return {
        id: payload.sub || payload.userId || '',
        email: payload.email || '',
        firstName: payload.firstName || undefined,
        lastName: payload.lastName || undefined,
        tenantId: payload.tenantId || 0,
        roles: roles
      };
    } catch {
      this.clearStoredData();
      return null;
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.getCurrentUser() !== null;
  }

  /**
   * Check if current user has Admin role
   */
  hasAdminRole(): boolean {
    const token = this.getStoredToken();

    if (!token) {
      return false;
    }

    try {
      // Parse the JWT token
      const parsedToken = JSON.parse(atob(token.split('.')[1]));

      // Parse the roles array
      const roles = JSON.parse(parsedToken.roles);

      // Check if user has Admin role
      return roles && roles.includes('Admin');
    } catch (error) {
      console.error('Error parsing JWT token for role check:', error);
      return false;
    }
  }

  /**
   * Check if current user has SuperAdmin role
   */
  hasSuperAdminRole(): boolean {
    const token = this.getStoredToken();

    if (!token) {
      return false;
    }

    try {
      // Parse the JWT token
      const parsedToken = JSON.parse(atob(token.split('.')[1]));

      // Parse the roles array
      const roles = JSON.parse(parsedToken.roles);

      // Check if user has SuperAdmin role
      return roles && roles.includes('SuperAdmin');
    } catch (error) {
      console.error('Error parsing JWT token for SuperAdmin check:', error);
      return false;
    }
  }

  /**
   * Get current token
   */
  getToken(): string | null {
    const token = this.getStoredToken();

    if (!token) {
      return null;
    }

    // Check if token is expired
    if (this.isTokenExpired(token)) {
      this.clearStoredData();
      return null;
    }

    return token;
  }

  /**
   * Store authentication data
   */
  setAuthData(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  /**
   * Get stored token
   */
  private getStoredToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }


  /**
   * Check if JWT token is expired
   */
  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp < currentTime;
    } catch {
      return true; // If we can't parse the token, consider it expired
    }
  }

  /**
   * Clear stored authentication data
   */
  private clearStoredData(): void {
    localStorage.removeItem(this.TOKEN_KEY);
  }

  /**
   * Logout user
   */
  logout(): void {
    this.clearStoredData();
  }

  /**
   * Get user display name (first and last name, or fallback to email)
   */
  getUserDisplayName(): string {
    const currentUser = this.getCurrentUser();
    if (!currentUser) {
      return '';
    }

    if (currentUser.firstName && currentUser.lastName) {
      return `${currentUser.firstName} ${currentUser.lastName}`;
    }

    return currentUser.firstName || currentUser.lastName || currentUser.email || '';
  }
}
